import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { IoChatbubbleEllipsesOutline, IoChevronDown, IoCloseOutline, IoHelpCircleOutline, IoLogOutOutline, IoMegaphoneOutline, IoMenuOutline, IoPulseOutline, IoStorefrontOutline, IoReceiptOutline } from 'react-icons/io5';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@modules/auth/api/auth.api';
import { reviewsApi } from '@modules/reviews/api/reviews.api';
import { SELLER_ME_QUERY_KEY, sellerOnboardingApi } from '@modules/seller-onboarding/api';
import { isSellerOwner, sellerHasSection, sellerCanOpenPickupPointsSection } from '@modules/seller-staff';
import { useSellerPendingOrConfirmedCount } from '@modules/orders';
import { SellerNotificationsBell } from '@shared/components/SellerNotificationsBell/SellerNotificationsBell';
import { SellerSupportChatToastListener } from '@modules/seller-support-chat/components/SellerSupportChatToastListener';
import { SellerOrderChatToastListener } from '@modules/client-order-chat/components/SellerOrderChatToastListener';
import { SellerNewOrderToastListener } from '@modules/orders/components/SellerNewOrderToastListener';
import { SellerOfferAcceptanceModal } from '@modules/seller-onboarding/components/SellerOfferAcceptanceModal';
import { sellerSupportChatApi } from '@modules/seller-support-chat/api/seller-support-chat.api';
import { clientOrderChatApi } from '@modules/client-order-chat/api/client-order-chat.api';
import { ORDER_CHAT_UNREAD_QUERY_KEY } from '@modules/client-order-chat/chat-cache';
import { sellerFinanceApi } from '@modules/seller-finance/api/seller-finance.api';
import { DoubleCheckModal } from '@shared/ui';
import { authUtils } from '@shared/utils/axios-instance';
import { clearSellerClientState } from '@shared/utils/clear-seller-client-state';
import { devProxyS3Url } from '@shared/utils/dev-proxy-s3-url';
import { sellerHelpUrl } from '@shared/utils/seller-help-url';
import { syncSellerWebPushWithServer } from '@modules/seller-notifications/web-push';
import './Layout.css';

type SidebarNavLinkProps = {
  to: string;
  className: string;
  disabled?: boolean;
  children: ReactNode;
};

function SidebarNavLink({ to, className, disabled, children }: SidebarNavLinkProps) {
  if (disabled) {
    return (
      <span className={`${className} menu-item--disabled`.trim()} aria-disabled="true">
        {children}
      </span>
    );
  }
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

type CollapsibleNavGroupProps = {
  label: string;
  icon?: ReactNode;
  open: boolean;
  onToggle: () => void;
  showDot?: boolean;
  children: ReactNode;
};

function CollapsibleNavGroup({ label, icon, open, onToggle, showDot, children }: CollapsibleNavGroupProps) {
  return (
    <div className={`sidebar-nav-group sidebar-nav-group--collapsible${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="sidebar-nav-group__toggle"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="sidebar-nav-group__label">
          {icon ? <span className="sidebar-nav-group__icon">{icon}</span> : null}
          {label}
          {showDot && !open ? (
            <span className="menu-item-dot" title="Есть важное уведомление" />
          ) : null}
        </span>
        <IoChevronDown className="sidebar-nav-group__chevron" aria-hidden />
      </button>
      {open ? <ul className="sidebar-nav-group__list">{children}</ul> : null}
    </div>
  );
}

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: sellerApp } = useQuery({
    queryKey: SELLER_ME_QUERY_KEY,
    queryFn: () => sellerOnboardingApi.getMe(),
  });
  const shopName =
    sellerApp?.seller?.shopName?.trim() || sellerApp?.shopName?.trim() || 'Luxus flowers';
  const logoUrl = sellerApp?.logoFileUrl?.trim() || null;
  const sellerAccountBlocked =
    sellerApp?.status === 'APPROVED' && Boolean(sellerApp?.seller?.accountBlock);
  const cabinetApproved =
    sellerApp?.status === 'APPROVED' &&
    sellerApp?.sellerId != null &&
    !sellerAccountBlocked;
  /** Колокольчик и inbox доступны и при блокировке аккаунта. */
  const notificationsAvailable =
    sellerApp?.status === 'APPROVED' && sellerApp?.sellerId != null;
  const supportChatAvailable = Boolean(
    sellerApp &&
    (sellerApp.status === 'APPROVED' ||
      sellerApp.status === 'PENDING_MODERATION' ||
      sellerApp.status === 'REJECTED'),
  );
  const owner = isSellerOwner(sellerApp);
  const sellerApprovedForOrders =
    cabinetApproved && sellerHasSection(sellerApp, 'ORDERS');
  const { data: pendingConfirmedData } = useSellerPendingOrConfirmedCount(sellerApprovedForOrders);
  const ordersAttentionCount = pendingConfirmedData?.count ?? 0;
  const { data: advertisingCredit } = useQuery({
    queryKey: ['seller-advertising-credit'],
    queryFn: () => sellerFinanceApi.getMyAdvertisingCredit(),
    enabled: cabinetApproved && sellerHasSection(sellerApp, 'PROMOTION'),
    staleTime: 30_000,
  });
  const cpcLimitHit =
    advertisingCredit != null && advertisingCredit.availableForAdsRub <= 0;
  const cpcBudgetPaused = Boolean(advertisingCredit?.weeklyBudget?.paused);
  const cpcAttention = cpcLimitHit || cpcBudgetPaused;
  // Счётчик «новых отзывов» доступен только с JWT админа; в кабинете продавца не дергаем API
  const { data: newReviewsData } = useQuery({
    queryKey: ['reviews', 'new-count'],
    queryFn: () => reviewsApi.getNewCount(),
    enabled: false,
  });
  const newReviewsCount = newReviewsData?.count ?? 0;
  const { data: chatUnreadData } = useQuery({
    queryKey: ['seller-support-chat', 'unread-count'],
    queryFn: () => sellerSupportChatApi.getUnreadCount(),
    enabled: supportChatAvailable,
    staleTime: 0,
  });
  const chatUnreadCount = chatUnreadData?.count ?? 0;
  const { data: orderChatUnreadData } = useQuery({
    queryKey: ORDER_CHAT_UNREAD_QUERY_KEY,
    queryFn: () => clientOrderChatApi.getUnreadCount(),
    enabled: cabinetApproved,
    staleTime: 0,
  });
  const orderChatUnreadCount = orderChatUnreadData?.count ?? 0;

  const isActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const marketingRouteActive =
    isActive('/promotion') || isActive('/promo-codes') || isActive('/bonuses');
  const shopRouteActive =
    isActive('/profile') ||
    isActive('/contacts') ||
    isActive('/finance') ||
    isActive('/accesses');

  const [marketingOpen, setMarketingOpen] = useState(marketingRouteActive);
  const [shopOpen, setShopOpen] = useState(shopRouteActive);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (sellerApp?.sellerId == null) return;
    void syncSellerWebPushWithServer().catch(() => undefined);
  }, [sellerApp?.sellerId]);

  useEffect(() => {
    if (marketingRouteActive) setMarketingOpen(true);
  }, [marketingRouteActive]);

  useEffect(() => {
    if (shopRouteActive) setShopOpen(true);
  }, [shopRouteActive]);

  /** Пока заявка не одобрена (или одобрен, но кабинет заблокирован), доступны главная, профиль и чат поддержки. */
  const cabinetNavRestricted = Boolean(
    sellerApp &&
    (sellerApp.status !== 'APPROVED' || sellerAccountBlocked),
  );
  const isNavDisabled = (path: string) =>
    cabinetNavRestricted &&
    path !== '/' &&
    path !== '/profile' &&
    path !== '/support-chat';
  const showNav = (
    section:
      | 'PRODUCTS'
      | 'ORDERS'
      | 'REVIEWS'
      | 'CONTACTS'
      | 'PROMOTION'
      | 'PROMO_CODES'
      | 'BONUSES'
      | 'FINANCE'
      | 'OWNER',
  ) => {
    if (section === 'OWNER') {
      return owner;
    }
    return sellerHasSection(sellerApp, section);
  };

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const handleLogout = async () => {
    setLogoutLoading(true);
    authUtils.markManualLogout();
    try {
      await authApi.logout();
    } catch {
      // Игнорируем ошибку — выходим локально в любом случае
    }
    await clearSellerClientState(queryClient);
    authUtils.clearSellerSession();
    navigate('/login');
  };

  return (
    <div className={`layout${mobileNavOpen ? ' layout--nav-open' : ''}`}>
      <div className="layout-mobile-bar">
        <button
          type="button"
          className="layout-mobile-menu-btn"
          onClick={() => setMobileNavOpen((v) => !v)}
          aria-label={mobileNavOpen ? 'Закрыть меню' : 'Открыть меню'}
          aria-expanded={mobileNavOpen}
        >
          {mobileNavOpen ? <IoCloseOutline size={22} /> : <IoMenuOutline size={22} />}
        </button>
        <div className="layout-mobile-bar__title">{shopName}</div>
      </div>
      {mobileNavOpen ? (
        <button
          type="button"
          className="layout-mobile-backdrop"
          aria-label="Закрыть меню"
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}
      <div className="layout-container">
        <nav className={`sidebar${mobileNavOpen ? ' sidebar--open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-brand">
              {logoUrl ? (
                <img
                  src={devProxyS3Url(logoUrl) || logoUrl}
                  alt={`Логотип ${shopName}`}
                  className="sidebar-brand-logo"
                />
              ) : (
                <div className="sidebar-brand-logo sidebar-brand-logo--fallback" aria-hidden>
                  {shopName.trim().charAt(0).toUpperCase() || 'L'}
                </div>
              )}
              <div className="sidebar-brand-text">
                <p className="sidebar-brand-eyebrow">Кабинет продавца</p>
                <h2 className="sidebar-brand-title">{shopName}</h2>
              </div>
            </div>
          </div>
          <div className="sidebar-menu">
            <div className="sidebar-nav-group">
              <div className="sidebar-nav-group__label">
                <span className="sidebar-nav-group__icon">
                  <IoPulseOutline />
                </span>
                Работа
              </div>
              <ul className="sidebar-nav-group__list">
                <li>
                  <SidebarNavLink
                    to="/"
                    disabled={isNavDisabled('/')}
                    className={`menu-item ${isActive('/') ? 'active' : ''}`}
                  >
                    Главная
                  </SidebarNavLink>
                </li>
                {showNav('ORDERS') ? (
                  <li>
                    <SidebarNavLink
                      to="/orders"
                      disabled={isNavDisabled('/orders')}
                      className={`menu-item ${isActive('/orders') ? 'active' : ''}`}
                    >
                      Заказы
                      {ordersAttentionCount > 0 && (
                        <span className="menu-item-badge" title="Ожидает или подтверждён">
                          {ordersAttentionCount > 99 ? '99+' : ordersAttentionCount}
                        </span>
                      )}
                    </SidebarNavLink>
                  </li>
                ) : null}
                {showNav('REVIEWS') ? (
                  <li>
                    <SidebarNavLink
                      to="/reviews"
                      disabled={isNavDisabled('/reviews')}
                      className={`menu-item ${isActive('/reviews') ? 'active' : ''}`}
                    >
                      Отзывы
                      {newReviewsCount > 0 && (
                        <span className="menu-item-badge" title="Новых отзывов">
                          {newReviewsCount > 99 ? '99+' : newReviewsCount}
                        </span>
                      )}
                    </SidebarNavLink>
                  </li>
                ) : null}
                {showNav('PRODUCTS') ? (
                  <li>
                    <SidebarNavLink
                      to="/products"
                      disabled={isNavDisabled('/products')}
                      className={`menu-item ${isActive('/products') ? 'active' : ''}`}
                    >
                      Товары
                    </SidebarNavLink>
                  </li>
                ) : null}
              </ul>
            </div>

            {showNav('PROMOTION') ||
            showNav('PROMO_CODES') ||
            showNav('BONUSES') ? (
              <CollapsibleNavGroup
                label="Маркетинг"
                icon={<IoMegaphoneOutline />}
                open={marketingOpen}
                onToggle={() => setMarketingOpen((v) => !v)}
                showDot={cpcAttention && showNav('PROMOTION')}
              >
                {showNav('PROMOTION') ? (
                <li>
                  <SidebarNavLink
                    to="/promotion"
                    disabled={isNavDisabled('/promotion')}
                    className={`menu-item ${isActive('/promotion') ? 'active' : ''}`}
                  >
                    Продвижение
                    {cpcAttention ? (
                      <span
                        className="menu-item-dot"
                        title={
                          cpcLimitHit
                            ? 'Рекламный баланс пуст'
                            : 'Лимит недельного бюджета продвижения достигнут'
                        }
                      />
                    ) : null}
                  </SidebarNavLink>
                </li>
                ) : null}
                {showNav('PROMO_CODES') ? (
                <li>
                  <SidebarNavLink
                    to="/promo-codes"
                    disabled={isNavDisabled('/promo-codes')}
                    className={`menu-item ${isActive('/promo-codes') ? 'active' : ''}`}
                  >
                    Промокоды
                  </SidebarNavLink>
                </li>
                ) : null}
                {showNav('BONUSES') ? (
                <li>
                  <SidebarNavLink
                    to="/bonuses"
                    disabled={isNavDisabled('/bonuses')}
                    className={`menu-item ${isActive('/bonuses') ? 'active' : ''}`}
                  >
                    Бонусная система
                  </SidebarNavLink>
                </li>
                ) : null}
              </CollapsibleNavGroup>
            ) : null}

            {showNav('OWNER') ||
            showNav('FINANCE') ||
            sellerCanOpenPickupPointsSection(sellerApp) ? (
              <CollapsibleNavGroup
                label="Магазин"
                icon={<IoStorefrontOutline />}
                open={shopOpen}
                onToggle={() => setShopOpen((v) => !v)}
              >
                {showNav('OWNER') ? (
                  <li>
                    <SidebarNavLink
                      to="/profile"
                      disabled={isNavDisabled('/profile')}
                      className={`menu-item ${isActive('/profile') ? 'active' : ''}`}
                    >
                      Мои данные
                    </SidebarNavLink>
                  </li>
                ) : null}
                {sellerCanOpenPickupPointsSection(sellerApp) ? (
                  <li>
                    <SidebarNavLink
                      to="/contacts"
                      disabled={isNavDisabled('/contacts')}
                      className={`menu-item ${isActive('/contacts') ? 'active' : ''}`}
                    >
                      Точки самовывоза
                    </SidebarNavLink>
                  </li>
                ) : null}
                {showNav('FINANCE') ? (
                  <li>
                    <SidebarNavLink
                      to="/finance"
                      disabled={isNavDisabled('/finance')}
                      className={`menu-item ${isActive('/finance') ? 'active' : ''}`}
                    >
                      Финансы
                    </SidebarNavLink>
                  </li>
                ) : null}
                {showNav('OWNER') ? (
                  <li>
                    <SidebarNavLink
                      to="/accesses"
                      disabled={isNavDisabled('/accesses')}
                      className={`menu-item ${isActive('/accesses') ? 'active' : ''}`}
                    >
                      Доступы
                    </SidebarNavLink>
                  </li>
                ) : null}
              </CollapsibleNavGroup>
            ) : null}
          </div>
          <div className="sidebar-footer">
            <ul className="sidebar-footer-menu">
              <li>
                <SidebarNavLink
                  to="/support-chat"
                  disabled={isNavDisabled('/support-chat')}
                  className={`menu-item ${isActive('/support-chat') ? 'active' : ''}`}
                >
                  <span className="menu-item__main">
                    <span className="menu-item__icon" aria-hidden>
                      <IoChatbubbleEllipsesOutline />
                    </span>
                    Чат с поддержкой
                  </span>
                  {chatUnreadCount > 0 && !isActive('/support-chat') ? (
                    <span className="menu-item-dot" title="Есть непрочитанные сообщения" />
                  ) : null}
                </SidebarNavLink>
              </li>
              {cabinetApproved ? (
                <li>
                  <SidebarNavLink
                    to="/order-chats"
                    disabled={isNavDisabled('/order-chats')}
                    className={`menu-item ${isActive('/order-chats') ? 'active' : ''}`}
                  >
                    <span className="menu-item__main">
                      <span className="menu-item__icon" aria-hidden>
                        <IoReceiptOutline />
                      </span>
                      Чаты по заказам
                    </span>
                    {orderChatUnreadCount > 0 && !isActive('/order-chats') ? (
                      <span className="menu-item-dot" title="Есть непрочитанные сообщения" />
                    ) : null}
                  </SidebarNavLink>
                </li>
              ) : null}
              <li>
                <a
                  href={sellerHelpUrl()}
                  className="menu-item"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="menu-item__main">
                    <span className="menu-item__icon" aria-hidden>
                      <IoHelpCircleOutline />
                    </span>
                    Помощь
                  </span>
                </a>
              </li>
            </ul>
            <button
              type="button"
              className="menu-item logout-btn"
              onClick={() => setLogoutConfirmOpen(true)}
            >
              <span className="menu-item__main">
                <span className="menu-item__icon" aria-hidden>
                  <IoLogOutOutline />
                </span>
                Выйти
              </span>
            </button>
          </div>
        </nav>
        <main className="main-content">
          <Outlet />
        </main>
      </div>
      <DoubleCheckModal
        isOpen={logoutConfirmOpen}
        onClose={() => {
          if (!logoutLoading) setLogoutConfirmOpen(false);
        }}
        title="Выйти из кабинета?"
        message="Сессия завершится, и потребуется снова войти по номеру телефона."
        firstButtonText="Отмена"
        secondButtonText="Выйти"
        secondButtonAction={() => {
          void handleLogout();
        }}
        isLoading={logoutLoading}
      />
      <div className="layout-floating-notifications" aria-live="polite">
        <SellerNotificationsBell enabled={notificationsAvailable} />
      </div>
      <SellerSupportChatToastListener enabled={supportChatAvailable} />
      <SellerOrderChatToastListener enabled={cabinetApproved} />
      <SellerNewOrderToastListener
        enabled={notificationsAvailable}
        ordersEnabled={sellerApprovedForOrders}
      />
      <SellerOfferAcceptanceModal sellerApp={sellerApp} />
    </div>
  );
}

